/**
 * Configuration: admins only. One tab per section from the server's
 * catalogue (backend/app/services/settings.py); each setting renders by its
 * type, so a feature adds a setting there and it appears here. Each kind of
 * research gets a screen of its own (Business Opportunities: its AI model,
 * dimensions, matrix and prompt versions). The screens that are configuration too
 * (CONFIG_TABS) come first, then the settings sections; the first screen is
 * the default. The open tab is in `?tab=`, so it links.
 */

import { Settings, SlidersHorizontal } from 'lucide-react';

import { PageHead } from '@/ui/PageHead';
import { LoadFailure, LoadingCard, StateCard } from '@/ui/StateCard';
import { SectionForm } from './SectionForm';
import { useConfigurationTabs, type ConfigurationTabs } from './useConfigurationTabs';

function TabBar({ tabs }: { tabs: ConfigurationTabs }) {
  const { tabsShown, sections, isActive, setActive } = tabs;
  return (
    <div className="tabs card-tabs" role="tablist" aria-label="Configuration">
      {tabsShown.map(({ key, name, icon: Icon }) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={isActive(key)}
          className={`tab${isActive(key) ? ' is-active' : ''}`}
          onClick={() => setActive(key)}
        >
          <Icon aria-hidden="true" />
          {name}
        </button>
      ))}
      {sections.map((section) => (
        <button
          key={section.key}
          type="button"
          role="tab"
          aria-selected={isActive(section.key)}
          className={`tab${isActive(section.key) ? ' is-active' : ''}`}
          onClick={() => setActive(section.key)}
        >
          <Settings aria-hidden="true" />
          {section.name}
          <span className="count">{section.settings.length}</span>
        </button>
      ))}
    </div>
  );
}

function TabContent({ tabs }: { tabs: ConfigurationTabs }) {
  const { screen, settings, current } = tabs;
  if (screen) return <screen.Screen />;
  if (settings.isPending) return <LoadingCard label="Loading configuration" />;
  if (settings.error) return <LoadFailure error={settings.error} onRetry={() => void settings.refetch()} />;
  if (!current) return null;
  if (current.settings.length === 0) {
    return (
      <StateCard icon={<SlidersHorizontal aria-hidden="true" />} title={`No ${current.name.toLowerCase()} options yet`}>
        <p>{current.description}</p>
      </StateCard>
    );
  }
  return <SectionForm key={current.key} section={current} />;
}

export function ConfigurationPage() {
  const tabs = useConfigurationTabs();

  // The house list layout: one white card, the tab bar as its first row. A
  // tab's own cards sit flat inside it (`.config-card` in app.css).
  return (
    <>
      <PageHead title="Configuration" subtitle="Platform-wide settings" />
      <div className="card list-card config-card">
        <TabBar tabs={tabs} />
        <TabContent tabs={tabs} />
      </div>
    </>
  );
}
